import * as Sentry from '@sentry/react';

export const CHUNK_RELOAD_KEY = 'gp:chunk-reload-once';

// Allows auto-reload again later in the same tab session (e.g. after another deployment).
export const DEFAULT_CHUNK_RELOAD_TTL_MS = 5 * 60 * 1000;

function getStringProp(obj: unknown, key: string): string {
  if (!obj || typeof obj !== 'object') return '';
  if (!(key in obj)) return '';
  const value = (obj as Record<string, unknown>)[key];
  return typeof value === 'string' ? value : '';
}

export function getErrorMessage(error: unknown): string {
  if (!error) return '';
  if (typeof error === 'string') return error;
  if (error instanceof Error) return error.message;
  return getStringProp(error, 'message');
}

export function isProbablyChunkLoadError(error: unknown): boolean {
  const msg = getErrorMessage(error).toLowerCase();
  const name = getStringProp(error, 'name').toLowerCase();

  return (
    name.includes('chunkloaderror') ||
    msg.includes('chunkloaderror') ||
    msg.includes('loading chunk') ||
    msg.includes('failed to fetch dynamically imported module') ||
    msg.includes('importing a module script failed') ||
    msg.includes('failed to load module script')
  );
}

function readLastReloadTs(): number | null {
  try {
    const raw = window.sessionStorage.getItem(CHUNK_RELOAD_KEY);
    if (!raw) return null;
    const ts = Number(raw);
    return Number.isFinite(ts) ? ts : null;
  } catch {
    return null;
  }
}

export function canAttemptChunkReload(ttlMs: number = DEFAULT_CHUNK_RELOAD_TTL_MS): boolean {
  if (typeof window === 'undefined') return false;
  const lastTs = readLastReloadTs();
  if (lastTs === null) return true;
  return Date.now() - lastTs >= ttlMs;
}

type AttemptChunkReloadOptions = {
  ttlMs?: number;
  source?: string;
  routeImportPath?: string;
};

function devLog(message: string, extra?: Record<string, unknown>) {
  // eslint-disable-next-line no-console
  if (import.meta.env.DEV) console.warn(message, extra ?? {});
}

function buildChunkErrorEvent(
  error: unknown,
  opts: AttemptChunkReloadOptions,
  ctx?: Record<string, unknown>,
) {
  const msg = getErrorMessage(error);
  const errObj = error instanceof Error ? error : new Error(msg || 'Chunk load error');

  Sentry.withScope((scope) => {
    scope.setTag('error_kind', 'chunk_load');
    if (opts.source) scope.setTag('chunk_reload_source', opts.source);
    if (opts.routeImportPath) scope.setContext('route', { importPath: opts.routeImportPath });
    if (typeof window !== 'undefined') {
      scope.setContext('chunk_env', {
        href: window.location.href,
        userAgent: window.navigator.userAgent,
      });
    }
    if (ctx) scope.setContext('chunk_recovery', ctx);
    scope.setExtra('raw_error_message', msg);
    Sentry.captureException(errObj);
  });
}

export function reportChunkLoadError(
  error: unknown,
  opts: AttemptChunkReloadOptions = {},
  ctx?: Record<string, unknown>,
) {
  if (!isProbablyChunkLoadError(error)) return;

  buildChunkErrorEvent(error, opts, ctx);
  devLog('[chunk-load] chunk load error', { ...opts, ...ctx });
}

export function attemptChunkReloadForError(
  error: unknown,
  opts: AttemptChunkReloadOptions = {},
): boolean {
  if (typeof window === 'undefined') return false;
  if (!isProbablyChunkLoadError(error)) return false;

  const ttlMs = opts.ttlMs ?? DEFAULT_CHUNK_RELOAD_TTL_MS;
  const canReload = canAttemptChunkReload(ttlMs);
  if (!canReload) {
    // Still report to Glitchtip/Sentry so we can see frequency/reasons even when reload is throttled.
    reportChunkLoadError(error, opts, { action: 'reload_skipped_ttl', ttlMs });
    return false;
  }

  devLog('[chunk-load] reloading to recover from chunk error', { ...opts, ttlMs });

  try {
    window.sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  } catch {
    // ignore storage failures; we'll still attempt a reload
  }

  window.location.reload();
  return true;
}
